import React from "react";
import * as PhosphorIcons from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const fmt = (n) =>
  new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(n || 0);
const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("it-IT");
  } catch {
    return iso?.slice(0, 10) || "";
  }
};

const STATUS_LABELS = {
  created: { label: "In attesa di assegnazione", color: "bg-slate-100 text-slate-700" },
  assigned: { label: "Assegnato", color: "bg-blue-100 text-blue-700" },
  in_progress: { label: "In lavorazione", color: "bg-amber-100 text-amber-800" },
  waiting_approval: { label: "Attesa approvazione", color: "bg-purple-100 text-purple-800" },
  standby: { label: "In standby", color: "bg-orange-100 text-orange-700" },
};

/** Tab "In lavorazione" del Marketplace: pratiche attive del professionista. */
export default function PracticesInProgress({ inProgress, properties }) {
  if (inProgress.length === 0) {
    return (
      <div className="kpi-card text-center py-16" data-testid="in-progress-empty">
        <PhosphorIcons.Package size={48} className="mx-auto mb-4 text-muted-foreground" weight="duotone" />
        <p className="text-muted-foreground">Nessun servizio in lavorazione.</p>
        <p className="text-xs text-muted-foreground mt-2">I servizi acquistati appariranno qui.</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="in-progress-list">
      {inProgress.map((p) => {
        const prop = properties.find((pr) => pr._id === p.property_id);
        const propAddress = prop ? `${prop.address}${prop.city ? ", " + prop.city : ""}` : p.property_address || "—";
        const propOwner = prop ? (prop.owner_type === "azienda" ? (prop.owner_company_name || "") : `${prop.owner_first_name || ""} ${prop.owner_last_name || ""}`.trim()) : "";
        const st = STATUS_LABELS[p.status] || { label: p.status || "—", color: "bg-slate-100 text-slate-700" };
        const isShared = !!p.is_shared_with_client;
        const paymentPaid = p.payment_status === "paid" || p.payment_status === "via_plafond";
        const paymentBadge = isShared
          ? {
              label: paymentPaid ? "💳 Pagato dal cliente" : "⏳ Attesa pagamento cliente",
              color: paymentPaid ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800",
            }
          : { label: "💰 Pagato con plafond", color: "bg-purple-100 text-purple-800" };

        return (
          <div key={p._id} className="kpi-card fade-up space-y-3" data-testid={`in-progress-card-${p._id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-sm truncate">{p.service_name}</div>
                <div className="text-[11px] text-muted-foreground truncate mt-0.5">{propAddress}</div>
                {propOwner && <div className="text-[11px] text-slate-700 truncate mt-0.5">👤 {propOwner}</div>}
              </div>
              <div className="text-right shrink-0">
                <div className="font-mono font-bold text-purple-700 text-sm">{fmt(p.total_price || 0)}</div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2 text-[11px]">
              <div>
                <div className="uppercase tracking-wider text-muted-foreground font-bold text-[9px]">Data acquisto</div>
                <div className="font-medium">{fmtDate(p.created_at)}</div>
              </div>
              <div>
                <div className="uppercase tracking-wider text-muted-foreground font-bold text-[9px]">Step attuale</div>
                <div className="font-medium truncate">{p.current_step_name || "—"}</div>
              </div>
            </div>

            <div className="flex flex-wrap gap-1.5">
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${st.color}`}>{st.label}</span>
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${paymentBadge.color}`}>
                {paymentBadge.label}
              </span>
            </div>

            {isShared && p.payment_status !== "paid" && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="w-full text-xs"
                onClick={() => {
                  const base = process.env.REACT_APP_BACKEND_URL || window.location.origin;
                  const url = `${base}/pay/${p._id}`;
                  navigator.clipboard.writeText(url).then(
                    () => toast.success("Link cliente copiato"),
                    () => toast.error("Errore copia"),
                  );
                }}
                data-testid={`copy-share-${p._id}`}
              >
                <PhosphorIcons.Copy size={12} className="mr-1" /> Copia link cliente
              </Button>
            )}
          </div>
        );
      })}
    </div>
  );
}
